import { FC, PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { PermissionMap, RoleMap } from 'src/constants';
import { selectAuthUserInfo, useAuthUserInfo } from 'src/redux/auth/selectors';
import { Permission } from './types';

export type PermissionGuardProps = PropsWithChildren<{
  permission: Permission;
}>;

export const useHasPermission = (permission: Permission) => {
  const userData = useSelector(selectAuthUserInfo);

  return !!userData && PermissionMap[RoleMap[userData.role]].indexOf(permission) >= 0;
};

export const PermissionGuard: FC<PermissionGuardProps> = ({ children, permission }) => {
  const hasPermission = useHasPermission(permission);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return hasPermission ? <>{children}</> : null;
};

export function withRequiredPermission(permission: Permission) {
  return <P, C extends React.ComponentType<P>>(component: C): React.FC<P> => {
    const Component = component as any;

    return (props: P) => (
      <PermissionGuard permission={permission}>
        <Component {...props} />
      </PermissionGuard>
    );
  };
}

export function useSentinelAccess(): 'loading' | 'denied' | 'granted' {
  const userData = useAuthUserInfo();

  if (!userData) return 'loading';

  // Users with organizations that are active and inactive can use Sentinel Link
  if (!userData.orgDetails?.status || userData.orgDetails.status === 'deleted') {
    console.warn("User's org is deleted", userData);
    return 'denied';
  }

  if (userData.status !== 'active') {
    console.warn('user is not active', userData);
    return 'denied';
  }

  return 'granted';
}
